import { EventEmitter } from "@angular/core";
import { AlulaReceiver } from "@app/project/model/alula-receiver";
import { AlulaReceiverBind } from "@app/project/model/alula-receiver-bind";
import { ActionPayloadGeneric } from "@app/root-store/actions";
import {
    createCrudActionSets,
    createDeletePromptActionSet,
    createNoPropsAction,
    createSuccessAction,
} from "@app/root-store/actions/action-creators";
import { Action } from "@ngrx/store";

/**
 * A payload used when performing an operation on a single receiver bing
 */
export type ActionPayload = ActionPayloadGeneric & {
    receiverBind: AlulaReceiverBind;
    receiver?: AlulaReceiver;
    callback: EventEmitter<Action>;
};

/**
 * A payload used when loading a receiver bind
 */
interface LoadActionPayload {
    dealerId: string;
}

/**
 * An action result that returns a collection of receiver binds
 */
export interface BulkActionResult {
    receiverBinds: AlulaReceiverBind[];
}

/**
 * An action result that returns a single receiver bind
 */
export interface ActionResult {
    receiverBind?: AlulaReceiverBind;
    receiver?: AlulaReceiver;
    callback: EventEmitter<Action>;
}

/**
 * @description Used in the action creators to generate an action type like:
 * "[Entity Management] Load Entity"
 */
const actionCategory = "Alula Receiver Bind";

/**
 * Create the CRUD set of actions associated with the action category.
 * Must send in appropriate payload types for a single and load and result types for single and bulk.
 * A request, success and failure action are created for each CRUD operation.
 * Each action set should be deconstructed into consts from the result.
 */
const {
    load: loadSet,
    create: createSet,
    update: updateSet,
    delete: deleteSet,
} = createCrudActionSets<ActionPayload, LoadActionPayload, ActionResult, BulkActionResult>(actionCategory);

/**
 * Create each action from the deconstructed action sets created by createCrudActionSets.
 * Each action is strongly typed and should be used in accordance with the NgRx docs.
 * Any action is optional and can be omitted by simply not desctructuring it.
 */
export const { request: load, success: loadSuccess, failure: loadFailure } = loadSet;
export const { request: create, success: createSuccess, failure: createFailure } = createSet;
export const { request: update, success: updateSuccess, failure: updateFailure } = updateSet;
export const { request: deleteItem, success: deleteSuccess, failure: deleteFailure } = deleteSet;

/**
 * An action set to prompt a user when deleting an item
 */
export const { confirm: deleteConfirmation, dismiss: deleteDismiss } = createDeletePromptActionSet<ActionPayload>(actionCategory);

export const getSuccess = createSuccessAction<ActionResult>(actionCategory, "set");

/**
 * An action to clear the data in the store
 */
export const clear = createNoPropsAction(actionCategory, "Clear State");
